import React from "react"
import * as Yup from 'yup'
import Ajax from "../../../Ajax"
import { useFormik } from "formik"
import Field from "../fields/Field"
import { Button } from "@mui/material"
import UserFormOnly from "./UserForms"
import { AlertModal, DisplayDictionary } from "../../.."
import { store } from "../../../stores/Store"
import Account from "../../../models/Account"
import { format } from "date-fns"
import { UTCDate } from "@date-fns/utc"


interface AccountEditFormProps {
    account: Account;
    onSuccess?: () => void;
}


interface AccountCreateFormProps {
    initialValues?: any;
    title?: string;
    guidance?: string;
    parentUid?: string;
    onSuccess?: (node) => void;
}

export function AccountEditForm ({account, onSuccess = () => {}}: AccountEditFormProps) {

    const handleSubmit = async (values) => {

        var isSponsor = account.typeName === "Aora.Nationality.Data.SponsorAccountNode"

        return await Ajax.Node.Edit(isSponsor ? "sponsor" : "account", values).then(() => onSuccess())
    }

    if (!account) return <h4>Error loading form</h4>

    return (
        <AccountFormOnly
            initialValues={account}
            handleCancel={() => store.AlertStore.Close()}
            handleSubmit={(values) => handleSubmit(values)} />
    )
}


export function CustomerAccountCreateForm ({
    onSuccess = (node) => {}
}: AccountCreateFormProps) {

    const [step, setStep] = React.useState<0|1>(0)
    const [newAccount, setNewAccount] = React.useState<Account>(null)

    const handleSubmit = async (userValues) => {

        newAccount.childrenObjects = [{...userValues}]

        return await Ajax.Node.Create("account", newAccount.parent.uid, newAccount).then((response) => {
            onSuccess(response.data)
        })
    }

    return (
        <div>
            <h2 style={{marginBottom: "15px"}}>Create new AORA Customer</h2>

            <AccountFormOnly
                hidden={step !== 0}
                initialValues={{accountType: "customer"}}
                handleCancel={() => store.AlertStore.Close()}
                handleSubmit={(values) => {
                    setNewAccount(values)
                    setStep(1)
                    return Promise.resolve()
                }} />

            <UserFormOnly
                guidance="Enter the details of the account contact, you can decide whether they can sign in or not"
                hidden={step !== 1}
                allowWithoutCredentials={false}
                handleCancel={() => setStep(0)}
                handleSubmit={(values) => handleSubmit(values)} />
        </div>
    )
}


export function SponsorCreateForm ({
    parentUid,
    onSuccess = (node) => {}
}: AccountCreateFormProps) {

    const handleSubmit = async (accountFormValues) => {

        // Add blank user
        // accountFormValues.childrenObjects = [{
        //     firstName: "System user",
        //     familyName: accountFormValues.accountName,
        //     sex: "M",
        //     title: "MR",
        //     email: "info@aoralaw.com",
        //     phone: "",
        //     username: null,
        //     password: null,
        //     isAccountManager: false,
        //     active: false,
        //     sendInvitationEmail: false
        // }]

        return await Ajax.Node.Create("sponsor", accountFormValues.parent.uid, accountFormValues).then((response) => {
            onSuccess(response.data)
        })
    }

    return (
        <div>
            <h2 style={{marginBottom: "15px"}}>Create new Sponsor</h2>

            <AccountFormOnly
                hidden={false}
                initialValues={{accountType: "sponsor", parentUid: parentUid}}
                handleCancel={() => store.AlertStore.Close()}
                handleSubmit={(values) => {
                    return handleSubmit(values)
                }} />
        </div>
    )
}


export default function AccountFormOnly ({
    handleSubmit,
    handleCancel,
    guidance = null,
    initialValues = null,
    hidden = false
}) {

    const editMode = !!initialValues?.uid

    const [error, setError] = React.useState<string>("")
    const [submitting, setSubmitting] = React.useState(false)
    // const [parentAccount, setParentAccount] = React.useState<Account>(null)
    const parentAccountUid = initialValues.accountType === "customer" ? "ACCOUNT_-1" : store.AppStore.sessionInfo.userAccount.uid

    // React.useEffect(() => {
    //     if (parentAccountUid !== null) {
    //         Ajax.Node.Get(parentAccountUid).then(response => {
    //             setParentAccount(response.data)
    //         }).catch(() => {
    //             console.log("Failed to get parent account", parentAccountUid)
    //         })
    //     }
    // }, [])

    var defaultInitialValues = {
        accountName: "",
        accountType: "",
        customerType: "",
        addressLine1: "",
        city: "",
        postcode: "",
        countryId: "UK",
        phone: "",
        regulator: "",
        regulatorRegistrationNumber: "",
        sponsorLicenceNum: "",
        parentUid: parentAccountUid,
    }

    // Allow some or all default values to be overridden
    if (initialValues) {
        initialValues = {...defaultInitialValues, ...initialValues}
    } else {
        initialValues = defaultInitialValues
    }

    var validationSchema = {
        accountName: Yup.string().required("This field is required"),
        addressLine1: Yup.string().required("This field is required"),
        city: Yup.string().required("This field is required"),
        postcode: Yup.string().required("This field is required"),
        countryId: Yup.string().when("accountType", {
            is: (accountType) => accountType !== "sponsor",
            then: () => Yup.string().required("This field is required"),
            otherwise: () => Yup.string().nullable()
        }),
        phone: Yup.string().matches(
            /^\s*(?:\+?(\d{1,4}))?([-. (]*(\d{2,4})[-. )]*)?((\d{2,4})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/,
            "A valid phone number is required"
        ).required("This field is required"),
        customerType: Yup.string().required("This field is required"),
        organisationType: Yup.string().when("customerType", {
            is: (customerType) => customerType === "N",
            then: () => Yup.string().required("This field is required for private sector organisations"),
            otherwise: () => Yup.string().nullable()
        }),
        sponsorLicenceNum: Yup.string().when("accountType", {
            is: (accountType) => accountType === "sponsor",
            then: () => Yup.string().required("This field is required").max(15, "Licence number must be less than 15 characters"),
            otherwise: () => Yup.string().nullable()
        }),
        // regulator: Yup.string().when("accountType", {
        //     is: (accountType) => accountType === "customer",
        //     then: () => Yup.string().required("This field is required"),
        //     otherwise: () => Yup.string().nullable()}),
        // regulatorRegistrationNumber: Yup.string().when("accountType", {
        //     is: (accountType) => accountType === "customer",
        //     then: () => Yup.string().required("This field is required"),
        //     otherwise: () => Yup.string().nullable()}),
    }

    var formik = useFormik({
        initialValues: initialValues,
        validationSchema: Yup.object(validationSchema),
        onSubmit: async (values) => {

            // Convert parentUid to node reference
            values.parent = {uid: values.parentUid}
            setError("")
            setSubmitting(true)

            return await handleSubmit(values).catch((response) => {

                setError(response.data?.detail ? response.data?.detail : "Something went wrong!")
                response.errors && formik.setErrors(response.errors)

            }).finally(() => setSubmitting(false))
        }
    })

    var organisationActivityOptions = DisplayDictionary("org_activity", true)
    // if (formik.values.countryId === "UK") {
    //     organisationActivityOptions = organisationActivityOptions.filter(option => !["IMM", "NAT"].includes(option.value))
    // }

    var regulatorOptions = DisplayDictionary("ni_regulator", true)
    regulatorOptions = [{value: null, label: "Unregulated"}, ...regulatorOptions]

    var showRegulatorFields = 
        (formik.values.accountType === "customer" && ['ACT', 'BAR', 'FIN', 'IMM', 'NAT', 'SOL'].includes(formik.values.organisationActivity))
        || (formik.values.accountType !== "customer" && ['ACT', 'FIN', 'IMM', 'NAT', 'SOL'].includes(formik.values.organisationActivity))

    if (hidden) return null

    return (
        <form autoComplete="off" className={`form ${submitting ? "disabled" : ""}`} onSubmit={formik.handleSubmit}>

            {guidance ? <p className="font-sm">{guidance}</p> : null}

            {formik.values.accountType === "sponsor" ? (
                <p className="font-sm">A Sponsor Client is a UK business which has a licence to sponsor Skilled Workers. Required for Skilled Worker enquiries.</p>
            ) : null}

            <div className="form-fields" style={{minHeight: "200px", maxHeight: "55vh", paddingRight: "5px"}}>

                <Field.Text
                    formik
                    autoFocus={true}
                    label={<><span style={{textTransform: "capitalize"}}>{formik.values.accountType}</span> name</>}
                    name="accountName"
                    value={formik.values.accountName}
                    warning={formik.submitCount && formik.errors.accountName ? formik.errors.accountName : false}
                    onChange={formik.handleChange} />

                <Field.Text
                    formik
                    label="Address"
                    name="addressLine1"
                    value={formik.values.addressLine1}
                    warning={formik.submitCount && formik.errors.addressLine1 ? formik.errors.addressLine1 : false}
                    onChange={formik.handleChange} />

                <Field.Text
                    formik
                    label="City"
                    name="city"
                    value={formik.values.city}
                    warning={formik.submitCount && formik.errors.city ? formik.errors.city : false}
                    onChange={formik.handleChange} />

                <Field.Text
                    formik
                    label="Postcode"
                    name="postcode"
                    value={formik.values.postcode}
                    warning={formik.submitCount && formik.errors.postcode ? formik.errors.postcode : false}
                    onChange={formik.handleChange} />

                {formik.values.accountType !== "sponsor" ? <Field.MinimisedLargeListSelect
                    formik
                    multiple={false}
                    label="Country"
                    name="countryId"
                    disabled={formik.values.accountType === "sponsor"}
                    value={formik.values.countryId}
                    warning={formik.submitCount && formik.errors.countryId ? formik.errors.countryId : false}
                    onChange={formik.handleChange}
                    options={DisplayDictionary("countries", true)} />
                : null}

                <Field.Text
                    formik
                    label="Phone number"
                    name="phone"
                    value={formik.values.phone}
                    warning={formik.submitCount && formik.errors.phone ? formik.errors.phone : false}
                    onChange={formik.handleChange} />

                <Field.Select
                    formik
                    name="customerType"
                    label="Customer type"
                    value={formik.values.customerType}
                    options={DisplayDictionary("customer_type", true)}
                    warning={formik.submitCount && formik.errors.customerType ? formik.errors.customerType : false}
                    onChange={(e) => {
                        formik.handleChange(e)
                        if (e.target.value !== "N") {
                            formik.setFieldValue("organisationType", "")
                            formik.setFieldValue("organisationActivity", "")
                            formik.setFieldValue("regulator", "")
                            formik.setFieldValue("regulatorRegistrationNumber", "")
                        }
                        formik.validateForm()
                    }} />

                {formik.values.customerType === "N" ? (
                    <Field.Select
                        formik
                        label="Private sector organisation type"
                        name="organisationType"
                        value={formik.values.organisationType}
                        warning={formik.submitCount && formik.errors.organisationType ? formik.errors.organisationType : false}
                        onChange={formik.handleChange}
                        options={DisplayDictionary("org_type", true)} />
                ) : null}

                {formik.values.customerType === "N" ? (
                    <Field.Select
                        formik
                        label="Organisation activity"
                        name="organisationActivity"
                        value={formik.values.organisationActivity}
                        warning={formik.submitCount && formik.errors.organisationActivity ? formik.errors.organisationActivity : false}
                        options={organisationActivityOptions}
                        onChange={(e) => {
                            formik.handleChange(e)
                            if (e.target.value === "OTH") {
                                formik.setFieldValue("regulator", "")
                                formik.setFieldValue("regulatorRegistrationNumber", "")
                            }
                            formik.validateForm()
                        }} />
                ) : null}

                {showRegulatorFields ? (
                    <>
                        <Field.Select
                            formik
                            label="Regulator"
                            name="regulator"
                            value={formik.values.regulator}
                            options={regulatorOptions}
                            warning={formik.submitCount && formik.errors.regulator ? formik.errors.regulator : false}
                            onChange={formik.handleChange} />
                    </>
                ) : <span></span>}

                {formik.values.regulator ? (
                    <Field.Text
                        formik
                        label="Regulator reference number"
                        name="regulatorRegistrationNumber"
                        value={formik.values.regulatorRegistrationNumber}
                        warning={formik.submitCount && formik.errors.regulatorRegistrationNumber ? formik.errors.regulatorRegistrationNumber : false}
                        onChange={formik.handleChange} />
                ) : null}

                {formik.values.accountType === "sponsor" ? (
                    <>
                        <Field.Text
                            formik
                            label="Sponsor licence number"
                            name="sponsorLicenceNum"
                            value={formik.values.sponsorLicenceNum}
                            warning={formik.submitCount && formik.errors.sponsorLicenceNum ? formik.errors.sponsorLicenceNum : false}
                            onChange={formik.handleChange} />
                    </>
                ) : <span></span>}

                {!editMode && store.AppStore.checkJwtRole("sysuser") && formik.values.accountType !== "customer" ? (
                    <Field.Select
                        formik
                        label="Parent account"
                        name="parentUid"
                        disabled={initialValues.uid}
                        value={formik.values.parentUid}
                        options={store.NodeStore.accounts.filter(s => !s.parent).map((a) => (
                            {label: a.label, value: a.uid}
                        ))}
                        warning={formik.submitCount && formik.errors.parentUid ? formik.errors.parentUid : false}
                        onChange={formik.handleChange} />
                ) : <span></span>}
            </div>

            {error ? <div className="form-message warning">{error}</div> : null}

            <div className="form-buttons">
                <Button
                    color="primary"
                    className="btn btn-sm"
                    onClick={() => handleCancel()}>
                    Cancel
                </Button>

                <Button
                    type="submit"
                    color="success"
                    variant="contained"
                    className="btn btn-sm"
                    disabled={formik.isSubmitting}>
                    Submit
                </Button>
            </div>

        </form>
    )
}